import type {
  ReactNode,
  ComponentPropsWithoutRef,
  JSX,
  Ref,
  FocusEvent,
  SubmitEvent,
  SyntheticEvent,
} from 'react';
import { forwardRef, useRef, useMemo, createContext, useState, useId } from 'react';
import { styled } from 'styled-components';
import { isNotUndefined, isUndefined } from '@wistia/type-guards';
import type { Action } from './useFormState';
import { Stack } from '../Stack';
import { serializeFormData } from './serializeFormData';

type StyledFormProps = {
  $fullWidth: boolean;
};

const StyledForm = styled.form<StyledFormProps>`
  --form-default-width: 690px;

  max-width: ${({ $fullWidth }) => ($fullWidth ? 'auto' : 'var(--form-default-width)')};
`;

type ValidationCallback<T> = (data: T) => FormErrors<T>;

type FormWithLabelledByProps = {
  'aria-label'?: never;
  /**
   * The id of the element that labels the form. Typically a heading.
   */
  'aria-labelledby': string;
};

type FormWithLabelProps = {
  /**
   * Accessible name of the form. Use this only when a visible label is not possible. If the form has a title or heading, use `aria-labelledby` instead.
   */
  'aria-label': string;
  'aria-labelledby'?: never;
};

export type FormErrors<T> = Partial<{
  [K in keyof T]: string[] | string;
}>;

export type FormProps<T> = Omit<
  ComponentPropsWithoutRef<'form'>,
  'action' | 'aria-label' | 'aria-labelledby' | 'children'
> & {
  /**
   * The action to be performed when the form is submitted. It can be asynchronous.
   */
  action?: Action;

  /**
   * Sets the default label position for all `FormFields` within the form. Will be ignored if labelPosition
   * is set on the `FormField` itself.
   */
  labelPosition?: 'block' | 'inline-compact' | 'inline';

  /**
   * The form elements used in the form
   */
  children: ReactNode;

  /**
   * An object that providesd errors for the form fields. This is created by `useFormState` and passed along to `FormField`s.
   */
  errors?: FormErrors<T>;

  /**
   * If true, fills the entire container
   */
  fullWidth?: boolean;

  /**
   * The function for handling validation.
   */
  validate?: ValidationCallback<T>;

  /**
   * An object that provides valuesfor the form fields. This is typically the `state.data` value from `useFormState` and passed along to `FormField`s.
   */
  values?: T;
} & (FormWithLabelledByProps | FormWithLabelProps);

type FormContextProps = {
  labelPosition: 'block' | 'inline';
  values: Record<string, unknown>;
  errors: FormErrors<Record<string, string>>;
  hasSubmitted: boolean;
  formId: string;
};

export const FormContext = createContext<FormContextProps>({
  values: {},
  errors: {},
  hasSubmitted: false,
  formId: 'NO_FORM',
  labelPosition: 'block',
});

/**
 * For creating forms. It provides a context for the form fields to access the form state and validation.
 * It also provides a way to handle form submission and validation. It is built around [React 19's form APIs](https://react.dev/reference/react-dom/components/form).
 */
// eslint-disable-next-line react/display-name
export const Form = forwardRef(
  <T = Record<string, unknown>,>(
    {
      children,
      action,
      values = {} as T,
      labelPosition = 'block',
      validate,
      fullWidth = false,
      ...props
    }: FormProps<T>,
    forwardedRef?: Ref<HTMLFormElement>,
  ): JSX.Element => {
    const [errors, setErrors] = useState({} as FormErrors<T>);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const innerRef = useRef<HTMLFormElement | null>(null);
    const ref = forwardedRef ?? innerRef;
    const autoId = useId();

    const id = props.id ?? autoId;

    const handleValidate = (nextFormData: FormData) => {
      const nextData = serializeFormData(nextFormData) as T;

      if (isUndefined(validate)) {
        return {};
      }
      return validate(nextData);
    };

    const handleBlur = (event: FocusEvent<HTMLFormElement>) => {
      if (props.onBlur) {
        props.onBlur(event);
      }

      const formData = new FormData(event.currentTarget);

      if (isNotUndefined(validate)) {
        handleValidate(formData);
      }
    };

    const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();

      const formData = new FormData(event.currentTarget);

      const nextErrors = handleValidate(formData);

      const isValid = Object.keys(nextErrors).length === 0;

      setErrors(nextErrors);
      setHasSubmitted(true);

      if (isValid && props.onSubmit) {
        props.onSubmit(event);
      }

      if (isValid && action) {
        void action(formData);
      }
    };

    const handleReset = (event: SyntheticEvent<HTMLFormElement>) => {
      setErrors({});
      setHasSubmitted(false);

      if (props.onReset) {
        props.onReset(event);
      }

      if (action) {
        void action(null);
      }
    };

    const context = useMemo(() => {
      return {
        values,
        errors,
        hasSubmitted,
        formId: id,
        labelPosition,
      };
    }, [labelPosition, values, errors, id, hasSubmitted]);

    return (
      // @ts-expect-error - generic context providers are a giant pain
      <FormContext.Provider value={context}>
        <Stack
          {...props}
          ref={ref}
          $fullWidth={fullWidth}
          alignItems={fullWidth ? 'stretch' : 'flex-start'}
          gap="space-05"
          id={id}
          onBlur={handleBlur}
          onReset={handleReset}
          onSubmit={handleSubmit}
          renderAs={StyledForm}
        >
          {children}
        </Stack>
      </FormContext.Provider>
    );
  },
) as <T>(props: FormProps<T> & { ref?: Ref<HTMLFormElement> }) => JSX.Element;
